import React, { useCallback, useEffect, useMemo, useState } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { ConfirmDialog } from 'components/modals';
import notificationService from 'services/notificationService';
import { dineOpsService } from 'api/services/dineOpsService';
import type { DineOutletAdmin, DineOutletStation } from 'api/types';

/**
 * Which kitchen stations one outlet may send to (DO-312, spec 2.36 §8.3a).
 * Stations are shared by the outlets: the Juice Bar can make juice for the
 * restaurant and the juice counter. A station is unticked only when no dish
 * sold here still goes to it; the API says which dishes when it refuses.
 *
 * Grouped by kitchen, with a tick for the whole kitchen: an outlet usually takes
 * most of one kitchen and a station or two of another (the Bar, Pastry). Only the
 * outlet's property's stations are listed, plus any it already sends to.
 */
interface Props {
  isOpen: boolean;
  outlet: DineOutletAdmin | null;
  canEdit: boolean;
  onClose: () => void;
  onSaved: () => void;
  /**
   * Shown as a section of the outlet window (DineOpsOutletWorkspace) rather than as its own window:
   * no backdrop, header or close; saving stays in the section. Default: its own window.
   */
  embedded?: boolean;
  /** Embedded: unsaved edits in this section, so the outlet window can warn before closing. */
  onDirtyChange?: (dirty: boolean) => void;
}

interface Group { key: string; name: string; stations: DineOutletStation[] }

const DineOpsOutletStationsModal: React.FC<Props> = ({ isOpen, outlet, canEdit, onClose, onSaved, embedded = false, onDirtyChange }) => {
  const [stations, setStations] = useState<DineOutletStation[]>([]);
  const [ticked, setTicked] = useState<Set<string>>(new Set());
  const [snapshot, setSnapshot] = useState('');
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [serverError, setServerError] = useState<string | null>(null);
  const [saveConfirmOpen, setSaveConfirmOpen] = useState(false);
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false);

  useEffect(() => {
    if (!isOpen || !outlet) return;
    setServerError(null);
    setLoading(true);
    dineOpsService.getOutletStations(outlet.id)
      .then((list) => {
        setStations(list);
        const set = new Set(list.filter((s) => s.isAllowed).map((s) => s.stationId));
        setTicked(set);
        setSnapshot(JSON.stringify([...set].sort()));
      })
      .catch((err: any) => notificationService.toast.error(err?.response?.data?.message ?? 'The kitchen stations could not be loaded.'))
      .finally(() => setLoading(false));
  }, [isOpen, outlet]);

  // The API returns them by kitchen (order, name), stations without a kitchen last.
  const groups = useMemo(() => {
    const out: Group[] = [];
    for (const s of stations) {
      const key = s.kitchenId ?? '';
      let g = out.find((x) => x.key === key);
      if (!g) { g = { key, name: s.kitchenName ?? 'No kitchen set', stations: [] }; out.push(g); }
      g.stations.push(s);
    }
    return out;
  }, [stations]);

  const isDirty = !!snapshot && JSON.stringify([...ticked].sort()) !== snapshot;
  useEffect(() => { onDirtyChange?.(isDirty); }, [isDirty, onDirtyChange]);

  const toggle = (id: string) => setTicked((prev) => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });

  const toggleGroup = (g: Group, on: boolean) => setTicked((prev) => {
    const next = new Set(prev);
    for (const s of g.stations) { if (on) next.add(s.stationId); else next.delete(s.stationId); }
    return next;
  });

  const handleClose = useCallback(() => {
    if (isDirty) setDiscardConfirmOpen(true);
    else onClose();
  }, [isDirty, onClose]);

  const save = async () => {
    if (!outlet) return;
    setSaveConfirmOpen(false);
    setSaving(true);
    setServerError(null);
    try {
      await dineOpsService.replaceOutletStations(outlet.id, [...ticked]);
      notificationService.toast.success(`Kitchen stations for "${outlet.outletName}" saved.`);
      setSaving(false);
      onSaved();
      // Embedded, the section stays open on what was saved.
      if (embedded) setSnapshot(JSON.stringify([...ticked].sort()));
      else onClose();
    } catch (err: any) {
      setSaving(false);
      setServerError(err?.response?.data?.message ?? 'The kitchen stations could not be saved.');
    }
  };

  if (!isOpen || !outlet) return null;

  const overlayEl = (saving || loading) && (
          <div style={{ position: 'absolute', inset: 0, background: 'var(--modal-loading-overlay)', display: 'flex',
            alignItems: 'center', justifyContent: 'center', zIndex: 10, borderRadius: 'inherit' }}>
            <span style={{ fontSize: '0.85rem', color: 'var(--text-secondary)', fontWeight: 500 }}>{saving ? 'Saving...' : 'Loading...'}</span>
          </div>
        );
  const body = (
    <>
        <p style={{ fontSize: 13, color: 'var(--text-secondary)', marginTop: 0 }}>
          Tick the kitchen stations this outlet may send tickets to. Stations are shared, so one kitchen can serve several outlets.
        </p>
        {serverError && <div className="form-error-message" style={{ marginBottom: 12 }}>{serverError}</div>}
        <div style={{ maxHeight: 420, overflowY: 'auto', border: '1px solid var(--border-color)', borderRadius: 6 }}>
          {stations.length === 0 && !loading && <div style={{ padding: 16, color: 'var(--text-muted)' }}>No kitchen stations yet. Add them on Restaurant → Settings → Kitchens.</div>}
          {groups.map((g) => {
            const all = g.stations.every((s) => ticked.has(s.stationId));
            return (
              <div key={g.key || 'none'} data-kitchen-group>
                <label style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '6px 12px', background: 'var(--bg-secondary)',
                  borderBottom: '1px solid var(--border-color)', fontSize: 12, fontWeight: 600, cursor: canEdit ? 'pointer' : 'default' }}>
                  <input type="checkbox" checked={all} disabled={!canEdit}
                    onChange={() => toggleGroup(g, !all)} aria-label={`All stations of ${g.name}`} />
                  <span style={{ flex: 1 }}>{g.name}</span>
                  <span style={{ fontWeight: 400, color: 'var(--text-muted)' }}>{g.stations.filter((s) => ticked.has(s.stationId)).length} of {g.stations.length}</span>
                </label>
                {g.stations.map((s) => (
                  <label key={s.stationId} data-station-row style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px 8px 32px',
                    borderBottom: '1px solid var(--border-color)', cursor: canEdit ? 'pointer' : 'default' }}>
                    <input type="checkbox" checked={ticked.has(s.stationId)} disabled={!canEdit}
                      onChange={() => toggle(s.stationId)} aria-label={`Allow ${s.stationName}`} />
                    <span style={{ flex: 1 }}>
                      <strong>{s.stationName}</strong>
                      <span style={{ color: 'var(--text-muted)', fontSize: 12 }}> · {s.stationCode}{s.isOpen ? '' : ' · closed now'}</span>
                    </span>
                    <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>
                      {s.dishCount > 0 ? `${s.dishCount} dish${s.dishCount === 1 ? '' : 'es'} here` : '—'}
                    </span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
    </>
  );
  const dialogs = (
    <>
    <ConfirmDialog open={saveConfirmOpen} title="Confirm Save"
      message={`Save the kitchen stations "${outlet.outletName}" may send to? ${ticked.size} ticked.`}
      confirmLabel="Confirm" cancelLabel="Cancel" variant="default" onConfirm={() => void save()} onCancel={() => setSaveConfirmOpen(false)} />
    <ConfirmDialog open={discardConfirmOpen} title="Discard Changes?" message="You have unsaved changes. Do you want to discard them?"
      confirmLabel="Discard" cancelLabel="Keep Editing" variant="danger"
      onConfirm={() => { setDiscardConfirmOpen(false); onClose(); }} onCancel={() => setDiscardConfirmOpen(false)} />
    </>
  );

  if (embedded) {
    return (
      <div className="outlet-ws-pane">
        {overlayEl}
        {body}
        {canEdit && (
          <div className="outlet-ws-actions">
            <button className="btn btn-secondary" disabled={!isDirty || saving}
              onClick={() => setTicked(new Set(JSON.parse(snapshot || '[]') as string[]))}>Undo changes</button>
            <button className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)} disabled={!isDirty || saving}
              title={!isDirty ? 'No changes to save' : undefined}>Save stations</button>
          </div>
        )}
        {dialogs}
      </div>
    );
  }

  return (
    <>
      <div className="modal-backdrop" />
      <div className="modal-container modal-md" style={{ minHeight: 'auto', maxWidth: 620 }}>
        {overlayEl}
        <div className="modal-header" style={{ minHeight: 'auto' }}>
          <h2 className="modal-title">Kitchen Stations — {outlet.outletName} ({outlet.outletCode})</h2>
          <button className="modal-close" onClick={handleClose} disabled={saving}><IconSystem.x size={20} /></button>
        </div>
        <div className="modal-content">
          {body}
        </div>
        <div className="modal-footer" style={{ minHeight: 'auto' }}>
          <button className="btn btn-secondary" onClick={handleClose} disabled={saving}>Cancel</button>
          {canEdit && (
            <button className="btn btn-primary" onClick={() => setSaveConfirmOpen(true)} disabled={!isDirty || saving}
              title={!isDirty ? 'No changes to save' : undefined}>Save</button>
          )}
        </div>
      </div>
      {dialogs}
    </>
  );
};

export default DineOpsOutletStationsModal;
